import type { SessionCapabilities } from './race';

export type DisplayQuality = 'fresh' | 'stale' | 'pending' | 'unknown' | 'unsupported' | 'deleted';

export interface DisplayValue<T> {
  value: T | null;
  quality: DisplayQuality;
  observedAtMs: number | null;
  lap: number | null;
  reason?: 'age' | 'coverage-gap' | 'not-started' | 'source-unsupported' | 'reset' | 'deleted';
}

export const DEFAULT_STALE_AFTER_MS = 15_000;

export function displayValue<T>(value: T | null, observedAtMs: number | null, cursorMs: number, options: {
  staleAfterMs?: number;
  lap?: number | null;
  reason?: DisplayValue<T>['reason'];
} = {}): DisplayValue<T> {
  if (options.reason === 'source-unsupported') return { value: null, quality: 'unsupported', observedAtMs: null, lap: options.lap ?? null, reason: options.reason };
  if (options.reason === 'deleted') return { value: null, quality: 'deleted', observedAtMs, lap: options.lap ?? null, reason: options.reason };
  if (value === null || value === undefined) return { value: null, quality: 'unknown', observedAtMs, lap: options.lap ?? null, reason: options.reason };
  const age = observedAtMs === null ? Number.POSITIVE_INFINITY : Math.max(0, cursorMs - observedAtMs);
  const staleAfterMs = options.staleAfterMs ?? DEFAULT_STALE_AFTER_MS;
  return { value, quality: age > staleAfterMs ? 'stale' : 'fresh', observedAtMs, lap: options.lap ?? null, ...(age > staleAfterMs ? { reason: 'age' as const } : {}) };
}

export function unsupportedValue<T>(capability: keyof SessionCapabilities, capabilities?: SessionCapabilities): DisplayValue<T> {
  const value = capabilities?.[capability] ?? 'unknown';
  return value === 'unsupported'
    ? { value: null, quality: 'unsupported', observedAtMs: null, lap: null, reason: 'source-unsupported' }
    : { value: null, quality: 'unknown', observedAtMs: null, lap: null };
}

export function withCoverageStale<T>(sample: DisplayValue<T>, cursorMs: number, reason: 'coverage-gap' = 'coverage-gap'): DisplayValue<T> {
  if (sample.value === null || sample.quality === 'unsupported' || sample.quality === 'deleted') return sample;
  return { ...sample, quality: 'stale', reason, observedAtMs: sample.observedAtMs === null ? cursorMs : sample.observedAtMs };
}
